import { Feature } from "ol";
import olView from "ol/View";
import { LineString, Point } from "ol/geom";
import { fromLonLat } from "ol/proj";
import { Fill, Stroke, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";
import { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getPingSession } from "../../Api/PingSessionApi";
import { LoadingSpinner } from "../../Components/LoadingSpinner/LoadingSpinner";
import PinPointNavbar from "../../Components/PinPointNavbar/PinPointNavbar";
import Layers from "../../MapComponents/Layers/Layers";
import TileLayer from "../../MapComponents/Layers/TileLayer";
import VectorLayer from "../../MapComponents/Layers/VectorLayer";
import Map from "../../MapComponents/Map/Map";
import Overlays from "../../MapComponents/Overlays/Overlays";
import TimelineSpeedOverlay from "../../MapComponents/Overlays/TimelineSpeedOverlay";
import MyVectorSource from "../../MapComponents/Source/VectorSource";
import osm from "../../MapComponents/Source/osm";
import "./SessionTimeline.css";

const WINDOW_MS = 5000;

const ROUTE_LINE_STYLE = new Style({
  stroke: new Stroke({ color: "rgba(60,60,60,0.35)", width: 1.5 }),
});

const TIMELINE_POINT_STYLE = new Style({
  image: new CircleStyle({
    radius: 6,
    fill: new Fill({ color: "#8255a6" }),
    stroke: new Stroke({ color: "#643787", width: 1.5 }),
  }),
});

const GHOST_POINT_STYLE = new Style({
  image: new CircleStyle({
    radius: 4,
    fill: new Fill({ color: "rgba(130, 85, 161, 0.25)" }),
    stroke: new Stroke({ color: "rgba(100, 55, 135, 0.35)", width: 1 }),
  }),
});

export const SessionTimeline = () => {
  const { pingSessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const view = useMemo(
    () =>
      new olView({
        center: fromLonLat([-0.099912, 51.513671]),
        zoom: 12,
      }),
    []
  );

  const [loadingMessage, setLoadingMessage] = useState(undefined);
  const [isLoaded, setIsLoaded] = useState(false);
  const [features, setFeatures] = useState([]);
  const [pingSessionData, setPingSessionData] = useState(null);
  const [sliderValue, setSliderValue] = useState(null);

  const [earliest, latest] = useMemo(() => {
    if (features.length === 0) return [null, null];

    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < features.length; i++) {
      const timestampMs = features[i].get("timestampMs");
      if (timestampMs < min) min = timestampMs;
      if (timestampMs > max) max = timestampMs;
    }
    return [min, max];
  }, [features]);

  const routeLineFeatures = useMemo(() => {
    if (features.length < 2) return [];

    const sorted = [...features].sort(
      (a, b) => a.get("timestampMs") - b.get("timestampMs")
    );
    const line = new Feature(
      new LineString(sorted.map((f) => f.getGeometry().getCoordinates()))
    );
    return [line];
  }, [features]);

  const filteredFeatures = useMemo(() => {
    if (sliderValue === null) return [];
    return features.filter(
      (f) => Math.abs(f.get("timestampMs") - sliderValue) <= WINDOW_MS
    );
  }, [features, sliderValue]);

  const sliderDisabled = earliest === null || earliest === latest;

  const selectionExtent = useMemo(() => {
    if (filteredFeatures.length === 0) return null;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    for (let i = 0; i < filteredFeatures.length; i++) {
      const [x, y] = filteredFeatures[i].getGeometry().getCoordinates();
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }

    return { minX, minY, maxX, maxY };
  }, [filteredFeatures]);

  const selectionCentroid = useMemo(() => {
    if (!selectionExtent) return null;
    const { minX, minY, maxX, maxY } = selectionExtent;
    return [(minX + maxX) / 2, (minY + maxY) / 2];
  }, [selectionExtent]);

  // Anchored at the horizontal center, bottom edge of the active pings'
  // extent, so the badge sits directly beneath the highlighted cluster.
  const selectionBadgePosition = useMemo(() => {
    if (!selectionExtent) return null;
    const { minX, minY, maxX } = selectionExtent;
    return [(minX + maxX) / 2, minY];
  }, [selectionExtent]);

  const averageSpeedKmh = useMemo(() => {
    const speeds = filteredFeatures
      .map((f) => f.get("speed"))
      .filter((s) => typeof s === "number" && !Number.isNaN(s));
    if (speeds.length === 0) return null;
    return speeds.reduce((sum, s) => sum + s, 0) / speeds.length;
  }, [filteredFeatures]);

  useEffect(() => {
    if (!selectionCentroid) return;
    view.setCenter(selectionCentroid);
  }, [selectionCentroid, view]);

  useEffect(() => {
    if (!pingSessionId) return;

    const handedOffSession = location.state?.pingSession;
    if (handedOffSession && String(handedOffSession.pingSessionId) === String(pingSessionId)) {
      // The session view already fetched this data — reuse it instead of
      // hitting the API again.
      applySession(handedOffSession);
    } else {
      loadTimelineData(pingSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pingSessionId]);

  useEffect(() => {
    if (earliest !== null) {
      setSliderValue(earliest);
    }
  }, [earliest]);

  const applySession = (pingSession) => {
    setPingSessionData(pingSession);

    const builtFeatures = (pingSession.pings ?? []).map((ping) => {
      const point = new Point(
        fromLonLat([ping.position.coordinates[0], ping.position.coordinates[1]])
      );
      const feature = new Feature(point);
      feature.set("id", ping.pingId);
      feature.set("createDate", ping.createDate);
      feature.set("pingSessionId", ping.pingSessionId);
      feature.set("speed", ping.speedKilometresPerHour);
      feature.set("acceleration", ping.accelerationMetresPerSecond);
      feature.set("timestampMs", Date.parse(ping.createDate));
      return feature;
    });

    setFeatures(builtFeatures);

    if (builtFeatures.length > 0) {
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      for (let i = 0; i < builtFeatures.length; i++) {
        const cords = builtFeatures[i].getGeometry().flatCoordinates;
        const x = cords[0];
        const y = cords[1];
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }

      const buffer = 2000;
      const bufferedExtent = [minX - buffer, minY - buffer, maxX + buffer, maxY + buffer];
      view.fit(bufferedExtent, { duration: 1000 });
    }

    setLoadingMessage(undefined);
    setIsLoaded(true);
  };

  const loadTimelineData = (id) => {
    setLoadingMessage("Loading session data...");
    setIsLoaded(false);

    getPingSession(id).then((pingSession) => {
      applySession(pingSession);
    });
  };

  const handleSliderChange = (e) => {
    setSliderValue(Number(e.target.value));
  };

  const handleBack = () => {
    navigate(
      `/sessions/${pingSessionId}`,
      pingSessionData ? { state: { pingSession: pingSessionData } } : undefined
    );
  };

  return (
    <>
      {loadingMessage && <LoadingSpinner Message={loadingMessage} />}
      <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <PinPointNavbar showBackButton onBackClick={handleBack} />
        <div className="sessionTimelineMapContainer">
          <Map view={view} onClick={() => {}}>
            <Layers>
              <TileLayer source={osm()} zIndex={0} />
              <VectorLayer style={ROUTE_LINE_STYLE} zIndex={1}>
                <MyVectorSource features={routeLineFeatures} />
              </VectorLayer>
              <VectorLayer style={GHOST_POINT_STYLE} zIndex={2}>
                <MyVectorSource features={features} />
              </VectorLayer>
              <VectorLayer style={TIMELINE_POINT_STYLE} zIndex={3}>
                <MyVectorSource features={filteredFeatures} />
              </VectorLayer>
            </Layers>
            <Overlays>
              <TimelineSpeedOverlay
                position={selectionBadgePosition}
                averageSpeedKmh={averageSpeedKmh}
                pingCount={filteredFeatures.length}
              />
            </Overlays>
          </Map>
        </div>
        {isLoaded && (
          <div className="timeline-controls">
            {features.length === 0 && <p>No pings recorded for this session.</p>}
            {features.length === 1 && <p>Only one ping recorded for this session.</p>}
            {!sliderDisabled && (
              <>
                <div className="timeline-current-time">
                  {new Date(sliderValue).toLocaleString()}
                </div>
                <Form.Range
                  min={earliest}
                  max={latest}
                  step={1000}
                  value={sliderValue}
                  onChange={handleSliderChange}
                />
                <div className="timeline-bounds">
                  <span>{new Date(earliest).toLocaleString()}</span>
                  <span>{new Date(latest).toLocaleString()}</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};
